import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  linkedSignal,
  signal,
  viewChild,
} from '@angular/core';
import { ModalDialogComponent, ModalDialogService } from '@pedix-workspace/angular-ui-modal';
import { TranslocoDirective } from '@jsverse/transloco';
import {
  GeolocationResult,
  GeolocationService,
  StadiaMapsComponent,
  StadiaMapsPopupComponent,
  StadiaMarkerIcon,
} from '@pedix-workspace/angular-ui-map';
import { StadiaMapsMarkerComponent } from 'libs/angular/ui/map/src/lib/stadia-maps/stadia-maps-marker/stadia-maps-marker.component';
import { getDistanceFromLatLonInKm, LatLng } from '@pedix-workspace/utils';
import { GeoIpService } from '@pedix-workspace/pedixapp-core-services';
import {
  IconChevronRightComponent,
  IconCloseComponent,
  IconGeolocationComponent,
  IconLoadingComponent,
  IconStoreComponent,
} from '@pedix-workspace/angular-ui-icons';
import { environment } from 'apps/pedixapp/src/environments/environment';
import { ApiV1_Subsidiary } from '@pedix-workspace/api-v1';
import { SelectBoxComponent } from '@pedix-workspace/angular-ui-form-reactive';
import { FormsModule } from '@angular/forms';
import { AlertComponent, TagComponent } from '@pedix-workspace/angular-ui-alert';
import { ButtonComponent } from '@pedix-workspace/angular-ui-button';
import { EnvironmentService } from '@pedix-workspace/angular-utils';
import { GeolocationErrorDialogComponent } from '@pedix-workspace/pedixapp-shared-catalog';
import { EstablishmentInfoComponent } from '../../../main/side-menu/establishment-info/establishment-info.component';
import { EstablishmentOpenedStatusTagComponent } from '@pedix-workspace/pedixapp-shared-catalog';
import { AppRequestContextService } from '../../../../app-request-context.service';
import {
  SubsidiariesByGroup,
  getSortedSubsidiariesByGroupAndDistance,
} from '@pedix-workspace/pedixapp-shared-catalog';

export type SubsidiaryWithDistance = {
  subsidiary: ApiV1_Subsidiary;
  distance: number;
};
const MAX_DISTANCE_FOR_TO_SHOW_NEAREST_SUBSIDIARIES = 5;

@Component({
  selector: 'app-subsidiaries-selector-dialog',
  templateUrl: './subsidiaries-selector-dialog.component.html',
  styleUrls: ['./subsidiaries-selector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslocoDirective,
    ModalDialogComponent,
    StadiaMapsComponent,
    StadiaMapsMarkerComponent,
    StadiaMapsPopupComponent,
    IconLoadingComponent,
    SelectBoxComponent,
    FormsModule,
    IconStoreComponent,
    AlertComponent,
    ButtonComponent,
    IconGeolocationComponent,
    TagComponent,
    IconCloseComponent,
    IconChevronRightComponent,
    EstablishmentInfoComponent,
    EstablishmentOpenedStatusTagComponent,
  ],
})
export class SubsidiariesSelectorDialogComponent {
  private geoIpService = inject(GeoIpService);
  private geolocationService = inject(GeolocationService);
  private environmentService = inject(EnvironmentService);
  private modalDialogService = inject(ModalDialogService);
  private appRequestContextService = inject(AppRequestContextService);

  readonly modalDialog = viewChild.required(ModalDialogComponent);
  readonly map = viewChild(StadiaMapsComponent);

  readonly selectedSubsidiaryId = signal<string | null>(null);
  readonly subsidiaries = signal<ApiV1_Subsidiary[]>([]);
  readonly mapZoom = signal<number>(12);
  readonly userLocation = signal<LatLng | null>(null);
  readonly mapIsReady = signal(false);
  readonly loadingGeolocation = signal(false);
  readonly geolocationResult = signal<GeolocationResult | null>(null);
  readonly displaySubsidiariesGroupSelector = signal(false);

  readonly mapPinIcon = computed<StadiaMarkerIcon>(() => {
    const customUrl =
      this.appRequestContextService.subsidiaryParentCatalog()?.establishment?.mapPinImageUrl;

    return {
      width: 40,
      height: 45,
      url: customUrl || 'assets/images/store-map-pin.png',
    };
  });

  readonly selectedGroupKey = linkedSignal<string | null>(() => {
    const subsidiariesByGroup = this.subsidiariesByGroup();

    return subsidiariesByGroup[0]?.key || null;
  });

  readonly subsidiariesByGroup = computed<SubsidiariesByGroup[]>(() => {
    const userLocation = this.userLocation();
    const subsidiaries = this.subsidiaries();
    const selectedSubsidiaryId = this.selectedSubsidiaryId();

    return getSortedSubsidiariesByGroupAndDistance(
      subsidiaries,
      userLocation,
      selectedSubsidiaryId,
    );
  });
  readonly subsidiariesByGroupSelected = computed(() => {
    const selectedGroupKey = this.selectedGroupKey();

    return (
      this.subsidiariesByGroup().find(group => group.key === selectedGroupKey)?.subsidiaries || []
    );
  });
  readonly nearestSubsidiaries = computed<SubsidiaryWithDistance[]>(() => {
    const geolocationResult = this.geolocationResult();
    const subsidiaries = this.subsidiaries();

    if (!geolocationResult || !subsidiaries.length) {
      return [];
    }
    const subsidiariesWithDistance = subsidiaries.map(subsidiary => {
      const coordinates = subsidiary.addressDetails?.coordinates;
      const distance = coordinates
        ? getDistanceFromLatLonInKm(geolocationResult, coordinates)
        : Number.MAX_SAFE_INTEGER;
      return {
        subsidiary,
        distance: parseFloat(distance.toFixed(1)),
      };
    });

    return subsidiariesWithDistance
      .filter(
        subsidiaryWithDistance =>
          subsidiaryWithDistance.distance <= MAX_DISTANCE_FOR_TO_SHOW_NEAREST_SUBSIDIARIES,
      )
      .sort((a, b) => a.distance - b.distance)
      .slice(0, 5);
  });

  constructor() {
    effect(() => {
      const mapIsReady = this.mapIsReady();
      const subsidiariesByGroupSelected = this.subsidiariesByGroupSelected();
      const map = this.map();

      if (map && mapIsReady && subsidiariesByGroupSelected.length > 0) {
        map.fitBoundsToMarkers(this.getSubsidiariesCoordinates(subsidiariesByGroupSelected));
      }
    });
  }

  async open({
    subsidiaries,
    selectedSubsidiaryId,
    displaySubsidiariesGroupSelector,
  }: {
    subsidiaries: ApiV1_Subsidiary[];
    selectedSubsidiaryId: string | null;
    displaySubsidiariesGroupSelector?: boolean;
  }) {
    // NOTE: Filter out subsidiaries without address details to avoid errors in the map
    this.subsidiaries.set(
      subsidiaries.filter(subsidiary => !!subsidiary.addressDetails?.coordinates),
    );
    this.selectedSubsidiaryId.set(selectedSubsidiaryId);
    this.displaySubsidiariesGroupSelector.set(displaySubsidiariesGroupSelector ?? false);

    if (!this.userLocation()) {
      this.geoIpService.retrieveGeoIpState(environment.workerServicesUrl).then(geoIp => {
        this.userLocation.set(geoIp);
      });
    }

    return this.modalDialog()
      .open(async (selectedSubsidiaryId: string) => {
        return selectedSubsidiaryId;
      })
      .catch(() => null);
  }

  onMapLoaded() {
    setTimeout(() => {
      this.mapIsReady.set(true);

      if (this.userLocation()) {
        this.map().fitBoundsToMarkers(
          this.getSubsidiariesCoordinates(this.subsidiariesByGroupSelected()),
        );
      }
    }, 0);
  }

  onGeolocationResult(coordinates: GeolocationResult) {
    this.userLocation.set(coordinates);
    this.geolocationResult.set(coordinates);
  }

  async onDisplayNearestSubsidiaries() {
    this.loadingGeolocation.set(true);
    const geolocationResult = await this.geolocationService
      .geolocate()
      .then(async geolocationResult => {
        if (geolocationResult) {
          return geolocationResult;
        }
        if (this.environmentService.isLocalEnv) {
          return <LatLng>{
            lat: -31.3792301,
            lng: -64.2458837,
          };
        }
        await this.modalDialogService
          .attachDialog(GeolocationErrorDialogComponent)
          .open()
          .catch(() => {});

        return null;
      });
    this.loadingGeolocation.set(false);

    if (geolocationResult) {
      this.geolocationResult.set(geolocationResult);
    }
  }

  private getSubsidiariesCoordinates(subsidiaries: ApiV1_Subsidiary[]): LatLng[] {
    return subsidiaries
      .map(subsidiary => subsidiary.addressDetails?.coordinates)
      .filter((coordinates): coordinates is LatLng => !!coordinates);
  }
}
